/* @jsx h */
import { defineComponent, h, ref, computed, watch } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { RightSheet } from "@lemmonhq/uikit";
import { useTrpc } from "@app/vue/providers/index";
import type {
  Automation,
  AutomationTriggerEvent,
  AutomationCondition,
  AutomationMessageTemplate,
} from "@lemmonhq/types";

// ─── Config ──────────────────────────────────────────────────────────────────

const TRIGGER_OPTIONS: { value: AutomationTriggerEvent; label: string; icon: string; description: string }[] = [
  { value: "reservation_completed", label: "Reservation Completed",  icon: "event_available", description: "After a reservation is marked completed" },
  { value: "reservation_created",   label: "Reservation Booked",     icon: "event",           description: "When a new reservation is confirmed" },
  { value: "order_completed",       label: "Order Completed",        icon: "receipt_long",    description: "After an order is paid" },
  { value: "birthday",              label: "Birthday",               icon: "cake",            description: "On the customer's birthday morning" },
  { value: "member_created",        label: "Member Added to List",   icon: "group_add",       description: "When a customer is added to a membership list" },
];

const TEMPLATE_VARS: Record<AutomationTriggerEvent, string[]> = {
  reservation_completed: ["{{customer_name}}", "{{reservation_date}}", "{{party_size}}", "{{shop_name}}", "{{review_link}}"],
  reservation_created:   ["{{customer_name}}", "{{reservation_date}}", "{{party_size}}", "{{shop_name}}"],
  order_completed:       ["{{customer_name}}", "{{order_total}}", "{{shop_name}}"],
  birthday:              ["{{customer_name}}", "{{shop_name}}"],
  member_created:        ["{{customer_name}}", "{{program_name}}"],
};

const DEFAULT_BODIES: Record<AutomationTriggerEvent, string> = {
  reservation_completed: "Hi {{customer_name}}, thank you for dining with us at {{shop_name}}! We'd love to hear your feedback: {{review_link}}",
  reservation_created:   "Hi {{customer_name}}, your reservation at {{shop_name}} on {{reservation_date}} for {{party_size}} is confirmed.",
  order_completed:       "Hi {{customer_name}}, thank you for your order at {{shop_name}}! We hope to see you again soon.",
  birthday:              "Happy birthday, {{customer_name}}! 🎂 The team at {{shop_name}} wishes you a wonderful day.",
  member_created:        "Hi {{customer_name}}, welcome to {{program_name}}! Your membership card is attached — add it to your wallet and show it at the till.",
};

// ─── Component ────────────────────────────────────────────────────────────────

export const AutomationEditor = defineComponent<{
  automation?: Automation;
  scope?: "shop" | "group";
  onClose: () => void;
  onSaved: () => void;
}>({
  name: "AutomationEditor",
  props: ["automation", "scope", "onClose", "onSaved"],
  setup(props) {
    const trpc = useTrpc();
    const queryClient = useQueryClient();

    const form = ref({
      name:             props.automation?.name ?? "",
      trigger_event:    (props.automation?.trigger_event ?? "reservation_completed") as AutomationTriggerEvent,
      delay_minutes:    props.automation?.delay_minutes ?? 60,
      channels:         (props.automation?.channels ?? ["email"]) as string[],
      message_template: props.automation?.message_template ?? {
        subject: "Thank you for visiting us",
        body: DEFAULT_BODIES["reservation_completed"],
      } as AutomationMessageTemplate,
      conditions:       (props.automation?.conditions ?? []) as AutomationCondition[],
      review_link:      props.automation?.review_link ?? "",
      membership_program_id: props.automation?.membership_program_id ?? null as string | null,
    });

    // Membership programs (the "lists" a member_created automation can target).
    // Only fetched for the picker; enabled_only keeps archived lists out.
    const programsQuery = useQuery({
      queryKey: ["membership-programs", props.scope ?? "shop", "list"],
      queryFn: () => props.scope === "group"
        ? trpc.membershipPrograms.groupList.query({ enabled_only: true })
        : trpc.membershipPrograms.list.query({ enabled_only: true }),
      staleTime: 60_000,
    });
    const programs = computed(() => programsQuery.data?.value ?? []);

    // When trigger changes, suggest a new default body only if still using default
    const onTriggerChange = (event: AutomationTriggerEvent) => {
      const prev = form.value.trigger_event;
      form.value.trigger_event = event;
      if (form.value.message_template.body === DEFAULT_BODIES[prev]) {
        form.value.message_template = {
          ...form.value.message_template,
          body: DEFAULT_BODIES[event],
        };
      }
      // The list target only makes sense for member_created; drop it otherwise.
      // member_created sends immediately (no durable delay), so pin the delay to
      // 0 rather than carry a stale value the slider is hidden for.
      if (event === "member_created") form.value.delay_minutes = 0;
      else form.value.membership_program_id = null;
    };

    // Default the picker to the only list when there is exactly one, so the
    // common single-list shop can create a member_created automation in one tap.
    watch(
      [() => form.value.trigger_event, programs],
      ([trigger, list]) => {
        if (
          trigger === "member_created" &&
          !form.value.membership_program_id &&
          list.length === 1
        ) {
          form.value.membership_program_id = list[0].id;
        }
      },
      { immediate: true },
    );

    const createMutation = useMutation({
      mutationFn: () => {
        const input = {
        name:             form.value.name,
        trigger_event:    form.value.trigger_event,
        delay_minutes:    form.value.delay_minutes,
        channels:         form.value.channels as any,
        message_template: form.value.message_template,
        conditions:       form.value.conditions,
        review_link:      form.value.review_link || null,
        membership_program_id: form.value.trigger_event === "member_created" ? form.value.membership_program_id : null,
        };
        return props.scope === "group"
          ? trpc.shopAutomations.groupCreate.mutate(input)
          : trpc.shopAutomations.create.mutate(input);
      },
      onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["automations", props.scope ?? "shop"] }); props.onSaved(); props.onClose(); },
    });

    const updateMutation = useMutation({
      mutationFn: () => {
        const input = {
        id:               props.automation!.id,
        name:             form.value.name,
        trigger_event:    form.value.trigger_event,
        delay_minutes:    form.value.delay_minutes,
        channels:         form.value.channels as any,
        message_template: form.value.message_template,
        conditions:       form.value.conditions,
        review_link:      form.value.review_link || null,
        membership_program_id: form.value.trigger_event === "member_created" ? form.value.membership_program_id : null,
        };
        return props.scope === "group"
          ? trpc.shopAutomations.groupUpdate.mutate(input)
          : trpc.shopAutomations.update.mutate(input);
      },
      onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["automations", props.scope ?? "shop"] }); props.onSaved(); props.onClose(); },
    });

    const saving = computed(() => createMutation.isPending.value || updateMutation.isPending.value);
    const error  = computed(() => createMutation.error.value || updateMutation.error.value);
    const isMemberCreated = computed(() => form.value.trigger_event === "member_created");
    const isValid = computed(() =>
      form.value.name.trim().length > 0 &&
      form.value.message_template.body.trim().length > 0 &&
      (!isMemberCreated.value || Boolean(form.value.membership_program_id)),
    );

    const delayLabel = computed(() => {
      const m = form.value.delay_minutes;
      if (m === 0) return "Immediately";
      if (m < 60)  return `${m} min after`;
      if (m === 60) return "1 hour after";
      if (m < 1440) return `${m / 60} hours after`;
      return `${m / 1440} day${m / 1440 > 1 ? "s" : ""} after`;
    });

    const triggerCfg = computed(() => TRIGGER_OPTIONS.find((t) => t.value === form.value.trigger_event)!);
    const templateVars = computed(() => TEMPLATE_VARS[form.value.trigger_event]);
    const showReviewLink = computed(() => ["reservation_completed", "reservation_created"].includes(form.value.trigger_event));

    const toggleChannel = (ch: string) => {
      const chs = form.value.channels;
      if (chs.includes(ch)) { if (chs.length > 1) form.value.channels = chs.filter((c) => c !== ch); }
      else form.value.channels = [...chs, ch];
    };

    return () => (
      <RightSheet
        {...{
          isOpen: true,
          onClose: props.onClose,
          title: (
            <div class="flex flex-1 items-center text-xl text-accent">
              {props.automation ? "Edit Automation" : "New Automation"}
            </div>
          ),
        }}
      >
        <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div class="min-h-0 flex-1 overflow-y-auto p-6 space-y-6">
          {error.value && (
            <div class="alert alert-error text-sm">{(error.value as any)?.message ?? "Something went wrong"}</div>
          )}

          {/* Trigger */}
          <section class="space-y-3">
            <h3 class="text-sm font-semibold text-base-content/50 uppercase tracking-wider">Trigger</h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {TRIGGER_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onTriggerChange(opt.value)}
                  class={`flex items-start gap-3 p-3 rounded-xl border text-left transition-colors ${
                    form.value.trigger_event === opt.value
                      ? "border-primary bg-primary/5"
                      : "border-base-content/10 hover:border-base-content/30 bg-base-200/40"
                  }`}
                >
                  <span class={`material-symbols-outlined text-xl mt-0.5 ${form.value.trigger_event === opt.value ? "text-primary" : "text-base-content/40"}`}>
                    {opt.icon}
                  </span>
                  <div>
                    <div class={`text-sm font-medium ${form.value.trigger_event === opt.value ? "text-primary" : "text-accent"}`}>{opt.label}</div>
                    <div class="text-xs text-base-content/40 mt-0.5">{opt.description}</div>
                  </div>
                </button>
              ))}
            </div>
          </section>

          {/* List picker — only for member_created */}
          {isMemberCreated.value && (
            <section class="space-y-3">
              <h3 class="text-sm font-semibold text-base-content/50 uppercase tracking-wider">List</h3>
              {programsQuery.isLoading.value ? (
                <div class="flex items-center gap-2 text-sm text-base-content/50">
                  <span class="loading loading-spinner loading-sm" />
                  Loading lists…
                </div>
              ) : programs.value.length === 0 ? (
                <div class="rounded-xl border border-warning/30 bg-warning/5 p-3 text-sm text-base-content/70">
                  No membership lists yet. Create one under Membership Programs first.
                </div>
              ) : (
                <div class="form-control">
                  <label class="label pb-1">
                    <span class="label-text font-medium">Send when a member is added to *</span>
                  </label>
                  <select
                    value={form.value.membership_program_id ?? ""}
                    onChange={(e) => (form.value.membership_program_id = (e.target as HTMLSelectElement).value || null)}
                    class="select select-bordered select-sm focus:select-primary"
                  >
                    <option value="" disabled>Choose a list…</option>
                    {programs.value.map((p: { id: string; name: string }) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </section>
          )}

          {/* Basic info */}
          <section class="space-y-4">
            <h3 class="text-sm font-semibold text-base-content/50 uppercase tracking-wider">Details</h3>

            <div class="form-control">
              <label class="label pb-1"><span class="label-text font-medium">Name *</span></label>
              <input
                type="text"
                value={form.value.name}
                onInput={(e) => (form.value.name = (e.target as HTMLInputElement).value)}
                class="input input-bordered input-sm focus:input-primary"
                placeholder={`e.g. Post-visit review request`}
              />
            </div>

            {!isMemberCreated.value && (
              <div class="form-control">
                <label class="label pb-1">
                  <span class="label-text font-medium">Send delay</span>
                  <span class="label-text-alt text-primary font-medium">{delayLabel.value}</span>
                </label>
                <input
                  type="range"
                  min={0} max={2880} step={15}
                  value={form.value.delay_minutes}
                  onInput={(e) => (form.value.delay_minutes = parseInt((e.target as HTMLInputElement).value))}
                  class="range range-primary range-sm"
                />
                <div class="flex justify-between text-xs text-base-content/30 mt-1 px-0.5">
                  <span>Now</span><span>30m</span><span>1h</span><span>6h</span><span>24h</span><span>48h</span>
                </div>
              </div>
            )}

            <div class="form-control">
              <label class="label pb-1"><span class="label-text font-medium">Channels</span></label>
              <div class="flex gap-2 flex-wrap">
                {[
                  { value: "email",    label: "Email",    icon: "mail" },
                  { value: "whatsapp", label: "WhatsApp", icon: "chat" },
                  { value: "sms",      label: "SMS",      icon: "textsms" },
                ].map((ch) => (
                  <button
                    key={ch.value}
                    type="button"
                    onClick={() => toggleChannel(ch.value)}
                    class={`btn btn-sm gap-2 ${form.value.channels.includes(ch.value) ? "btn-primary" : "btn-ghost border border-base-content/20"}`}
                  >
                    <span class="material-symbols-outlined text-base">{ch.icon}</span>
                    {ch.label}
                  </button>
                ))}
              </div>
            </div>

            {showReviewLink.value && (
              <div class="form-control">
                <label class="label pb-1">
                  <span class="label-text font-medium">Review link</span>
                  <span class="label-text-alt text-base-content/40">used in {"{{review_link}}"}</span>
                </label>
                <input
                  type="url"
                  value={form.value.review_link}
                  onInput={(e) => (form.value.review_link = (e.target as HTMLInputElement).value)}
                  class="input input-bordered input-sm focus:input-primary"
                  placeholder="https://g.page/your-restaurant/review"
                />
              </div>
            )}
          </section>

          {/* Message */}
          <section class="space-y-3">
            <div class="flex items-center justify-between">
              <h3 class="text-sm font-semibold text-base-content/50 uppercase tracking-wider">Message</h3>
              <div class="flex flex-wrap gap-1">
                {templateVars.value.map((v) => (
                  <span
                    key={v}
                    class="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded font-mono cursor-pointer hover:bg-primary/20"
                    onClick={() => {
                      form.value.message_template = {
                        ...form.value.message_template,
                        body: form.value.message_template.body + " " + v,
                      };
                    }}
                    title="Click to insert"
                  >
                    {v}
                  </span>
                ))}
              </div>
            </div>

            {form.value.channels.includes("email") && (
              <div class="form-control">
                <label class="label pb-1"><span class="label-text font-medium">Subject</span></label>
                <input
                  type="text"
                  value={form.value.message_template.subject ?? ""}
                  onInput={(e) => (form.value.message_template = { ...form.value.message_template, subject: (e.target as HTMLInputElement).value })}
                  class="input input-bordered input-sm focus:input-primary"
                  placeholder="Thank you for visiting {{shop_name}}"
                />
              </div>
            )}

            <div class="form-control">
              <label class="label pb-1"><span class="label-text font-medium">Body *</span></label>
              <textarea
                value={form.value.message_template.body}
                onInput={(e) => (form.value.message_template = { ...form.value.message_template, body: (e.target as HTMLTextAreaElement).value })}
                class="textarea textarea-bordered focus:textarea-primary h-28 text-sm"
                placeholder={DEFAULT_BODIES[form.value.trigger_event]}
              />
            </div>
          </section>
        </div>
        <div class="sticky bottom-0 shrink-0 border-t bg-base-100 p-4">
          <button
            type="button"
            onClick={() =>
              props.automation
                ? updateMutation.mutate()
                : createMutation.mutate()
            }
            disabled={!isValid.value || saving.value}
            class="btn btn-primary btn-block"
          >
            {saving.value && (
              <span class="loading loading-spinner loading-sm mr-2" />
            )}
            {props.automation ? "Save" : "Create"}
          </button>
        </div>
        </div>
      </RightSheet>
    );
  },
});
